import type { TScreenRotation } from '@lucimark/shared/models/screen-rotation'
import { isTauriRuntime } from '@/helpers/isTauriRuntime'
import { getPlatform } from '@/platform'

export interface TApplyScreenOrientationResult {
  /** True only when the native Activity display actually matched the requested Lucimark angle. */
  applied: boolean
}

/**
 * Mirrors `KioskDisplayRotation.shouldSetActivityOrientation`.
 * Device-owner HDMI letterboxes a mismatched Activity (405×720 on H96); only pin when
 * the framebuffer already matches, or try Activity remap on phones.
 */
export function shouldSetAndroidActivityOrientation(
  isDeviceOwner: boolean,
  displayAlreadyMatches: boolean
): boolean {
  return displayAlreadyMatches || !isDeviceOwner
}

function parseApplied(raw: unknown): boolean {
  if (raw == null || typeof raw !== 'object') {
    return false
  }
  return (raw as Record<string, unknown>).applied === true
}

/**
 * Ask the Android shell to remap Activity orientation. Non-Android / missing plugin / verify
 * failure → `{ applied: false }` so the Vue player keeps today’s CSS path. Old APKs without
 * `applyScreenOrientation` still get `persistScreenRotation` for splash prefs.
 */
export async function applyAndroidScreenOrientation(
  rotation: TScreenRotation
): Promise<TApplyScreenOrientationResult> {
  if (!isTauriRuntime() || getPlatform() !== 'android') {
    return { applied: false }
  }
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    return {
      applied: parseApplied(
        await invoke<unknown>('plugin:lucimark-android|applyScreenOrientation', { rotation })
      )
    }
  }
  catch {
    try {
      const { invoke } = await import('@tauri-apps/api/core')
      await invoke('plugin:lucimark-android|persistScreenRotation', { rotation })
    }
    catch {
      // Bridge down or pre-splash-prefs APK — CSS path still works from localStorage.
    }
    return { applied: false }
  }
}
